import { motion } from 'motion/react';
import { useMemo } from 'react';

interface SocialProofInterstitialProps {
  sex?: string;
  motivation?: string;
}

// ─── Copy data ──────────────────────────────────────────────────────────────

const FEMALE_HEADLINES: Record<string, string> = {
  'general-health-curiosity': "You're already ahead of most people just by asking.",
  'cancer-runs-in-family': "You're right to pay attention to your family history.",
  'recent-health-scare': "A scare can be a wake-up call, and you're already taking the right step.",
  'being-proactive': 'Being proactive is the single best thing you can do for your health.',
  'doctor-recommended': 'Great, Catch gives you the data to have a more informed conversation.',
  'health-anxiety': "Uncertainty is the hardest part. Let's replace it with clarity.",
  'cancer-in-news': "You've been hearing the stories. Now let's make it personal to you.",
  other: "You're in the right place. Catch was built for people like you.",
};

const MALE_HEADLINE = "You're in the right place. Catch was built for people who want real answers.";
const HEADLINE_FALLBACK = 'Being proactive is the single best thing you can do for your health.';

const FEMALE_QUOTES: Record<string, string> = {
  'general-health-curiosity':
    '\u201cI just wanted to know where I stood. Catch gave me a clear picture of my cancer risk and simple steps to lower it. I wish I\u2019d done it sooner.\u201d',
  'cancer-runs-in-family':
    '\u201cMy mom had breast cancer at 52. I always wondered if I was next. Catch showed me exactly what to watch for and when to get screened. For the first time, I feel like I have a plan instead of just worry.\u201d',
  'recent-health-scare':
    '\u201cAfter a suspicious mole removal, I realized I had no idea what my actual cancer risk was. Catch gave me a full picture, not just skin cancer, but everything. It turned my fear into a clear action plan.\u201d',
  'being-proactive':
    '\u201cI track my sleep, my nutrition, my workouts, but I had a blind spot on cancer risk. Catch filled that gap completely. Now my health plan actually covers everything.\u201d',
  'doctor-recommended':
    '\u201cMy doctor said I was \u2018fine\u2019 but I wanted more details. Catch showed me biomarkers and screening timelines my doctor hadn\u2019t mentioned. I brought the report to my next visit and we made a real plan together.\u201d',
  'health-anxiety':
    '\u201cI used to spiral every time I Googled a symptom. Catch gave me real, personalized data instead of worst-case scenarios. Now I know what to actually focus on, and what I can stop worrying about.\u201d',
  'cancer-in-news':
    '\u201cI kept seeing articles about young people getting colon cancer and it scared me. Catch helped me understand my own risk level instead of just reading scary headlines. It was a relief to have real answers.\u201d',
  other:
    '\u201cI just wanted to know where I stood. Catch gave me a clear picture of my risk and simple steps to lower it. I wish I\u2019d done it sooner.\u201d',
};

const MALE_QUOTE =
  '\u201cI just wanted more clarity and data. Catch gave me a straightforward picture of my risk and simple steps to lower it. No drama, just useful information.\u201d';
const QUOTE_FALLBACK =
  '\u201cI track my sleep, my nutrition, my workouts, but I had a blind spot on cancer risk. Catch filled that gap completely. Now my health plan actually covers everything.\u201d';

const FEMALE_ATTRIBUTIONS: Record<string, string> = {
  'general-health-curiosity': '\u2013 Katie, 33',
  'cancer-runs-in-family': '\u2013 Sarah, 34',
  'recent-health-scare': '\u2013 Rachel, 38',
  'being-proactive': '\u2013 Jessica, 36',
  'doctor-recommended': '\u2013 Lauren, 42',
  'health-anxiety': '\u2013 Emma, 31',
  'cancer-in-news': '\u2013 Megan, 35',
  other: '\u2013 Katie, 33',
};

const MALE_ATTRIBUTION = '\u2013 James, 39';
const ATTRIBUTION_FALLBACK = '\u2013 Jessica, 36';

// ─── Personalization resolution ─────────────────────────────────────────────

function resolve(sex: string | undefined, motivation: string | undefined) {
  const isFemale = sex === 'female';
  const isMale = sex === 'male';
  const mot = motivation ?? 'other';

  const headline = isFemale
    ? (FEMALE_HEADLINES[mot] ?? HEADLINE_FALLBACK)
    : isMale
      ? MALE_HEADLINE
      : HEADLINE_FALLBACK;

  const quote = isFemale ? (FEMALE_QUOTES[mot] ?? QUOTE_FALLBACK) : isMale ? MALE_QUOTE : QUOTE_FALLBACK;

  const attribution = isFemale
    ? (FEMALE_ATTRIBUTIONS[mot] ?? ATTRIBUTION_FALLBACK)
    : isMale
      ? MALE_ATTRIBUTION
      : ATTRIBUTION_FALLBACK;

  const photo = isMale ? '/images/social-proof/testimonial-male.png' : '/images/social-proof/testimonial-female.jpg';

  return { headline, quote, attribution, photo };
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SocialProofInterstitial({ sex, motivation }: SocialProofInterstitialProps) {
  const { headline, quote, attribution, photo } = useMemo(() => resolve(sex, motivation), [sex, motivation]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-6">
      <motion.div
        className="flex w-full max-w-md flex-col gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ willChange: 'transform, opacity' }}
      >
        {/* Headline */}
        <h3 className="text-center text-2xl font-bold leading-tight text-white">{headline}</h3>

        {/* Testimonial card */}
        <div className="flex items-start gap-4 rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md">
          <img src={photo} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" draggable={false} />
          <div className="space-y-2">
            <p className="text-sm leading-relaxed text-white/90">{quote}</p>
            <p className="text-sm font-medium text-white/70">{attribution}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
